/**
 * Découpage du texte riche (sortie TipTap déjà passée par `sanitizeRichText`) en
 * blocs de premier niveau, + normalisation des espaces.
 *
 * ─── Pourquoi ce module existe ──────────────────────────────────────────────
 *
 * Le HTML riche ne peut PAS être injecté tel quel dans un conteneur `<p>` — et
 * le `<Text>` de react-email en est un. Un `<p>` (ou un `<h1>`) imbriqué dans un
 * `<p>` fait refermer le parent par TOUT parseur HTML : les blocs ressortent en
 * frères du conteneur et perdent sa typographie (taille, interligne, couleur).
 * Le rendu navigateur via `innerHTML` imbrique réellement, lui — d'où un aperçu
 * correct dans le builder et un mail envoyé « ramassé ».
 *
 * En découpant en blocs, chaque bloc est rendu par la primitive adéquate
 * (`<Text>` pour un paragraphe, `<Heading>` pour un titre) et porte ses propres
 * styles inline : plus d'imbrication invalide, et plus de dépendance à un
 * `<style>` que les clients mail suppriment ou tronquent.
 *
 * Deux pertes de contenu sont corrigées au passage, car elles se jouent au même
 * endroit :
 *  - un paragraphe vide (`<p></p>`, ce que produit TipTap quand on saute une
 *    ligne) a une hauteur nulle en HTML — l'éditeur dessine une ligne, le mail
 *    n'en montre aucune. On lui redonne un contenu insécable.
 *  - les espaces multiples et les espaces en début de ligne sont écrasés par le
 *    collapsing HTML standard. On les rend insécables.
 *
 * Les `values` d'un composant mail sont un record libre : ni `sanitizeRichTextFields`
 * ni le schéma ne les normalisent. Ce module doit donc tolérer du HTML qui n'a
 * jamais vu TipTap — gabarits par défaut (`<p style="…">`), collages, contenu poussé
 * par l'API — sans jamais faire confiance à ses attributs.
 */

/** Style inline d'un bloc, déjà filtré (clés camelCase prêtes pour un `style` React). */
export type RichTextInlineStyle = Record<string, string>;

export type RichTextListItem = {
  html: string;
  /** Sous-listes (TipTap : Tab dans un item) — rendues en `<ul>/<ol>` imbriqués dans le `<li>`. */
  nested?: RichTextList[];
};

export type RichTextList = { kind: 'list'; ordered: boolean; items: RichTextListItem[] };

/** Blocs de premier niveau que le texte riche peut produire. */
export type RichTextBlock =
  | { kind: 'paragraph'; html: string; style?: RichTextInlineStyle }
  | { kind: 'heading'; level: 1 | 2 | 3; html: string; style?: RichTextInlineStyle }
  | RichTextList;

/** Éléments qui ouvrent un bloc de premier niveau dans la sortie TipTap. */
const TOP_LEVEL_BLOCK_TAGS = new Set(['p', 'h1', 'h2', 'h3', 'ul', 'ol', 'div', 'blockquote']);

/** Détecte un bloc IMBRIQUÉ dans le contenu d'un autre bloc (cf. `splitRichTextBlocks`). */
const NESTED_BLOCK_PATTERN = new RegExp(`<(?:${[...TOP_LEVEL_BLOCK_TAGS].join('|')})\\b`, 'i');

/**
 * Garde-fou de récursion. Chaque niveau retire au minimum la balise ouvrante, donc la
 * descente termine toujours ; le plafond ne protège que d'une pile trop profonde sur un
 * HTML pathologique (`<div><div><div>…`). Au plafond, le niveau restant est APLATI
 * (balises de bloc retirées) — jamais recraché tel quel, sinon on réintroduirait
 * l'imbrication invalide que ce module existe pour supprimer.
 */
const MAX_NESTING_DEPTH = 6;

/** Éléments sans balise fermante — ils ne font jamais varier la profondeur. */
const VOID_TAGS = new Set(['br', 'img', 'hr', 'input', 'meta', 'source', 'col']);

/**
 * Attributs d'une balise, en respectant les valeurs entre guillemets : un `>` dans un
 * `href` (`?q=a>b`, que la sérialisation HTML n'échappe pas) ne doit pas fermer la balise.
 */
const TAG_ATTRS_PATTERN = `(?:[^>"']|"[^"]*"|'[^']*')*?`;

/** Groupes : 1 = `/` fermante, 2 = nom, 3 = attributs, 4 = `/` auto-fermante. */
const TAG_PATTERN = `<(/?)([a-z][a-z0-9]*)\\b(${TAG_ATTRS_PATTERN})(/?)>`;

/** Entité plutôt que le caractère U+00A0 : elle survit à toutes les moulinettes d'encodage des clients mail. */
const NBSP = '&nbsp;';

type ScannedNode = { tag: string | null; attrs: string; content: string };

/**
 * Parcourt `html` et renvoie ses éléments de premier niveau (selon `isBlockTag`)
 * dans l'ordre, les fragments inline hors bloc étant rendus avec `tag: null`.
 *
 * On compte la profondeur sur le nom de balise ouvert plutôt que d'utiliser une
 * regex gloutonne : `<ul><li><p>a</p></li></ul>` doit sortir UN bloc `ul`, pas
 * s'arrêter au premier `</p>`.
 */
function scanTopLevel(html: string, isBlockTag: (tag: string) => boolean): ScannedNode[] {
  const nodes: ScannedNode[] = [];
  const tagRe = new RegExp(TAG_PATTERN, 'gi');

  let depth = 0;
  let openTag = '';
  let openAttrs = '';
  let contentStart = 0;
  let cursor = 0;

  const pushLoose = (raw: string): void => {
    if (raw.length > 0) nodes.push({ tag: null, attrs: '', content: raw });
  };

  let match: RegExpExecArray | null;
  while ((match = tagRe.exec(html)) !== null) {
    const isClosing = match[1] === '/';
    const tag = match[2].toLowerCase();
    const isVoid = match[4] === '/' || VOID_TAGS.has(tag);

    if (depth === 0) {
      if (!isClosing && !isVoid && isBlockTag(tag)) {
        pushLoose(html.slice(cursor, match.index));
        depth = 1;
        openTag = tag;
        openAttrs = match[3];
        contentStart = match.index + match[0].length;
      }
      continue;
    }

    if (!isClosing && !isVoid && tag === openTag) {
      depth += 1;
      continue;
    }

    if (isClosing && tag === openTag) {
      depth -= 1;
      if (depth === 0) {
        nodes.push({ tag: openTag, attrs: openAttrs, content: html.slice(contentStart, match.index) });
        cursor = match.index + match[0].length;
      }
    }
  }

  // Balise jamais refermée (contenu tronqué / legacy) : on récupère le reste
  // comme contenu du bloc ouvert plutôt que de le perdre.
  if (depth > 0) nodes.push({ tag: openTag, attrs: openAttrs, content: html.slice(contentStart) });
  else pushLoose(html.slice(cursor));

  return nodes;
}

/** Espaces qu'un texte riche « vide » peut encore contenir après retrait des balises. */
const INVISIBLE_TEXT_PATTERN = /&nbsp;|&#160;|&#xa0;|[\s\u00a0\u200b]/gi;

/**
 * Le texte riche affiche-t-il quelque chose (texte ou image) ? Faux pour un contenu réduit à des
 * balises vides (`<p></p>`, `<p><br></p>`), ce que laisse un binding conditionnel `{{…_html}}` résolu
 * à vide dans un paragraphe TipTap. `splitRichTextBlocks` redonne une ligne insécable à un `<p></p>` :
 * sans ce test, un bloc « message cadeau » sans message s'affiche quand même (encadré vide).
 */
export function hasRichTextContent(html: string | undefined | null): boolean {
  if (!html) return false;
  if (/<img\b/i.test(html)) return true;
  return html.replace(/<[^>]*>/g, '').replace(INVISIBLE_TEXT_PATTERN, '').length > 0;
}

/** Nombre de `<br>` d'un fragment. */
function countLineBreaks(html: string): number {
  return html.match(/<br\b[^>]*>/gi)?.length ?? 0;
}

/**
 * Rend insécables les espaces que le collapsing HTML avalerait : ceux en début
 * de ligne (début de bloc ou après un `<br>`) et les suites de 2 espaces ou plus.
 * Le premier espace d'une suite reste sécable pour ne pas casser le retour à la
 * ligne automatique.
 */
export function preserveRichTextSpaces(html: string): string {
  const withLeading = html.replace(
    new RegExp('(^|<br\\s*/?>)( +)', 'gi'),
    (_match: string, prefix: string, spaces: string): string => `${prefix}${NBSP.repeat(spaces.length)}`,
  );

  // L'alternance « balise entière | texte entre balises » garantit qu'on ne
  // touche jamais au contenu d'un attribut (un `href` peut contenir des espaces).
  return withLeading.replace(new RegExp(`<${TAG_ATTRS_PATTERN}>|[^<]+`, 'g'), (segment: string): string => {
    if (segment.startsWith('<')) return segment;
    return segment.replace(/ {2,}/g, (run: string): string => ` ${NBSP.repeat(run.length - 1)}`);
  });
}

/**
 * Contenu inline prêt à être injecté dans un conteneur de bloc.
 * Un fragment vide devient un insécable : sans lui le bloc aurait une hauteur
 * nulle et la ligne sautée par l'utilisateur disparaîtrait du rendu.
 */
function toInlineHtml(html: string): string {
  if (!hasRichTextContent(html)) return NBSP;
  return preserveRichTextSpaces(html);
}

/**
 * Retire les balises de bloc d'un fragment destiné à un conteneur inline (le contenu
 * d'un `<h2>`). Même raison que la récursion de `splitRichTextBlocks` : un `<p>` dans
 * un `<h2>` referme le titre chez tout parseur. Ici on ne peut pas découper — le bloc
 * conteneur est déjà choisi — donc on aplatit.
 */
function stripBlockTags(html: string): string {
  return html.replace(new RegExp(`</?(?:${[...TOP_LEVEL_BLOCK_TAGS].join('|')}|li)\\b${TAG_ATTRS_PATTERN}>`, 'gi'), '');
}

/** Aplati les paragraphes d'un `<li>` en contenu inline séparé par des `<br>`. */
function unwrapParagraphs(html: string): string {
  return html
    .replace(new RegExp(`</p>\\s*<p\\b${TAG_ATTRS_PATTERN}>`, 'gi'), '<br />')
    .replace(new RegExp(`^\\s*<p\\b${TAG_ATTRS_PATTERN}>`, 'i'), '')
    .replace(/<\/p>\s*$/i, '');
}

/**
 * Propriétés CSS qu'un bloc stocké peut porter en inline. Liste fermée : le style vient
 * d'un HTML jamais assaini (gabarits par défaut, collage, API) et finit dans l'attribut
 * `style` de TOUS les mails sortants.
 */
const INLINE_STYLE_ALLOWED_PROPERTIES = new Set([
  'font-size',
  'font-weight',
  'font-style',
  'font-family',
  'letter-spacing',
  'line-height',
  'text-align',
  'text-decoration',
  'text-transform',
  'color',
  'background-color',
  'margin',
  'margin-top',
  'margin-bottom',
  'margin-left',
  'margin-right',
  'padding',
  'padding-top',
  'padding-bottom',
  'padding-left',
  'padding-right',
]);

/** Valeur CSS acceptable : ni délimiteur d'attribut ni entité (`&quot;`), ni `url()` / `expression()`. */
const INLINE_STYLE_SAFE_VALUE = /^[^;<>"'\\&]{1,120}$/;
const INLINE_STYLE_FORBIDDEN_VALUE = /url\s*\(|expression\s*\(|javascript:/i;

const toCamelCase = (property: string): string =>
  property.replace(/-([a-z])/g, (_match: string, letter: string): string => letter.toUpperCase());

function readAttribute(attrs: string, name: string): string | undefined {
  const match = new RegExp(`\\b${name}\\s*=\\s*(?:"([^"]*)"|'([^']*)'|([^\\s>]+))`, 'i').exec(attrs);
  if (!match) return undefined;
  return match[1] ?? match[2] ?? match[3];
}

/**
 * Style inline d'un bloc stocké, filtré sur une liste fermée de propriétés.
 * Les gabarits par défaut reposent dessus (`<p style="font-size:32px;text-align:center">`
 * pour un code OTP, `font-size:12px;color:#666` pour un pied de page) : le jeter
 * rendrait ces mails en corps de texte standard.
 */
function extractInlineStyle(attrs: string): RichTextInlineStyle | undefined {
  const style: RichTextInlineStyle = {};

  const align = readAttribute(attrs, 'align');
  if (align && /^(left|right|center|justify)$/i.test(align)) style.textAlign = align.toLowerCase();

  const rawStyle = readAttribute(attrs, 'style');
  if (rawStyle) {
    for (const declaration of rawStyle.split(';')) {
      const separator = declaration.indexOf(':');
      if (separator < 0) continue;
      const property = declaration.slice(0, separator).trim().toLowerCase();
      const value = declaration.slice(separator + 1).trim();
      if (!INLINE_STYLE_ALLOWED_PROPERTIES.has(property)) continue;
      if (!INLINE_STYLE_SAFE_VALUE.test(value) || INLINE_STYLE_FORBIDDEN_VALUE.test(value)) continue;
      style[toCamelCase(property)] = value;
    }
  }

  return Object.keys(style).length > 0 ? style : undefined;
}

/**
 * Pose `style` sur chaque `<a>` du fragment, en REMPLAÇANT un éventuel style existant
 * (un attribut dupliqué serait ignoré par les parseurs, qui gardent le premier).
 * Le style passé est de la responsabilité de l'appelant : il ne vient jamais du HTML stocké.
 */
export function setRichTextLinkStyle(html: string, style: string): string {
  return html.replace(new RegExp(`<a\\b(${TAG_ATTRS_PATTERN})>`, 'gi'), (_match: string, attrs: string): string => {
    const kept = attrs.replace(/\s*\bstyle\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]+)/gi, '');
    return `<a${kept} style="${style}">`;
  });
}

function parseListItem(content: string, depth: number): RichTextListItem {
  if (depth >= MAX_NESTING_DEPTH) return { html: toInlineHtml(stripBlockTags(unwrapParagraphs(content))) };

  const inlineParts: string[] = [];
  const nested: RichTextList[] = [];
  for (const node of scanTopLevel(content, (tag: string): boolean => tag === 'ul' || tag === 'ol')) {
    if (node.tag === null) {
      inlineParts.push(node.content);
      continue;
    }
    const list = parseList(node.tag === 'ol', node.content, depth + 1);
    if (list) nested.push(list);
  }

  const html = toInlineHtml(unwrapParagraphs(inlineParts.join('')));
  return nested.length > 0 ? { html, nested } : { html };
}

function parseList(ordered: boolean, content: string, depth: number): RichTextList | undefined {
  const items = scanTopLevel(content, (tag: string): boolean => tag === 'li')
    .filter((item: ScannedNode): boolean => item.tag === 'li')
    .map((item: ScannedNode): RichTextListItem => parseListItem(item.content, depth));
  return items.length > 0 ? { kind: 'list', ordered, items } : undefined;
}

function splitBlocks(html: string, depth: number): RichTextBlock[] {
  const blocks: RichTextBlock[] = [];

  for (const node of scanTopLevel(html, (tag: string): boolean => TOP_LEVEL_BLOCK_TAGS.has(tag))) {
    if (node.tag === null) {
      // Les blancs autour d'un fragment hors bloc sont de la mise en forme du HTML, pas
      // du contenu : un espace après un `</h1>` ne doit pas devenir un insécable en tête
      // de ligne. Un fragment fait uniquement de `<br>` (gabarit legacy) reste une ou
      // plusieurs lignes vides.
      const fragment = node.content.trim();
      if (hasRichTextContent(fragment)) {
        blocks.push({ kind: 'paragraph', html: toInlineHtml(fragment) });
      } else {
        const lineBreaks = countLineBreaks(fragment);
        if (lineBreaks > 0) blocks.push({ kind: 'paragraph', html: Array(lineBreaks).fill(NBSP).join('<br>') });
      }
      continue;
    }

    if (node.tag === 'ul' || node.tag === 'ol') {
      const list = parseList(node.tag === 'ol', node.content, depth);
      if (list) blocks.push(list);
      continue;
    }

    const style = extractInlineStyle(node.attrs);

    if (node.tag === 'h1' || node.tag === 'h2' || node.tag === 'h3') {
      // Un titre vide n'apporte rien : on le laisse tomber plutôt que d'imposer
      // une ligne de 36px dans le rendu.
      if (!hasRichTextContent(node.content)) continue;
      const level = Number(node.tag[1]) as 1 | 2 | 3;
      blocks.push({
        kind: 'heading',
        level,
        html: toInlineHtml(stripBlockTags(node.content)),
        ...(style && { style }),
      });
      continue;
    }

    // p / div / blockquote.
    // Un bloc peut en contenir un autre : `<div><p>a</p><p>b</p></div>`, ou le
    // `<p>a<h2>T</h2></p>` que produit un collage. Rendu tel quel dans un `<Text>`
    // (donc un `<p>`), on reproduirait EXACTEMENT l'imbrication invalide que ce module
    // existe pour supprimer. On ne descend QUE si un bloc imbriqué est présent : sinon
    // un `<p></p>` (la ligne vide voulue par l'utilisateur) serait dissous au lieu de
    // devenir un insécable.
    if (NESTED_BLOCK_PATTERN.test(node.content)) {
      if (depth < MAX_NESTING_DEPTH) blocks.push(...splitBlocks(node.content, depth + 1));
      else
        blocks.push({ kind: 'paragraph', html: toInlineHtml(stripBlockTags(node.content)), ...(style && { style }) });
      continue;
    }

    blocks.push({ kind: 'paragraph', html: toInlineHtml(node.content), ...(style && { style }) });
  }

  return blocks;
}

/**
 * Découpe du texte riche en blocs de premier niveau.
 *
 * Le contenu inline qui ne vit dans aucun bloc devient un paragraphe : c'est le
 * cas le plus fréquent en base, les gabarits par défaut stockant directement
 * `Bonjour,<br><br>…` sans jamais ouvrir de `<p>`.
 */
export function splitRichTextBlocks(html: string): RichTextBlock[] {
  if (!html) return [];
  return splitBlocks(html, 0);
}
